/**
 * Layer assembly — schema version plus `buildSystemLayers`, which walks the
 * `NSW_RLGSP_MODULES` table (see `layerConfig.ts`) and builds the
 * `INswRlgspSystemLayers` envelope from the flat operational-feature list.
 *
 * Per-layer config (style JSON carrying type/zoom/layout/paint, z-order,
 * query properties) lives on the table entries, not here — this file never
 * changes when a layer is added.
 */

import { getLogger } from '@logtape/logtape';
import { buildModuleLayers } from '#lib/common/moduleTable.js';
import type { TLayer } from '#lib/types.js';
import { getVectorLayerSource, groupByModule } from '#lib/common/layers.js';
import { EProduct, type INswRlgspFeature } from './interfaces';
import { moduleTable, type INswRlgspSystemLayers } from './layerConfig';

// GeoServer dataset this app owns; drives availability and layer filtering.
export const NSW_RLGSP_DATASET = 'nsw_rlgsp';

const log = getLogger(['speedmanagement', 'nsw-rlgsp', 'layers']);

/**
 * Build the nested `INswRlgspSystemLayers` from a flat list of operational
 * features. `appLayers` is expected to already be filtered to the active
 * version (see `loadSystem`); this keeps only the app dataset and groups it by
 * module.
 *
 * Returns null when product data is missing — caller treats this as a
 * system-data load failure.
 */
export function buildSystemLayers(appLayers: INswRlgspFeature[]): INswRlgspSystemLayers | null {
	const rlgspLayers = groupByModule(appLayers, EProduct.NswRlgsp, NSW_RLGSP_DATASET);
	if (!rlgspLayers) return null;

	// The style JSON is a MapLibre layer fragment; JSON imports widen `type`, so narrow here.
	return buildModuleLayers(
		moduleTable,
		rlgspLayers,
		(layerId, feature, def) =>
			getVectorLayerSource(layerId, feature, def.map.style as Partial<TLayer>, def.map.zIndex, {
				selectable: def.map.selectable,
				sharedSource: true
			}),
		log
	) as INswRlgspSystemLayers;
}
